import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, MoreHorizontal, Pencil, Boxes, PowerOff, Eye } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { useWithBack } from "@/lib/nav";
import StatusBadge from "@/components/StatusBadge";
import SearchInput from "@/components/SearchInput";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export default function MachineTypesList() {
  const withBackHref = useWithBack();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("machine_type.write");

  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (!showInactive) params.active = true;
      const { data } = await api.get("/machine-types", { params });
      setItems(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [search, showInactive]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const handleDeactivate = async (row) => {
    try {
      await api.delete(`/machine-types/${row.id}`);
      toast.success(`${row.name} deactivated`);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="p-6 space-y-6" data-testid="machine-types-page">
      <PageHeader
        testId="machine-types-header"
        title="Machine Types"
        subtitle="Templates that define checklists, parts, and default manuals for each machine class."
        actions={canWrite && (<Button asChild size="sm" data-testid="machine-type-new-button"><Link to="/machine-types/new"><Plus className="h-4 w-4" /> New Type</Link></Button>)}
      />

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <SearchInput value={search} onChange={setSearch} placeholder="Search types…" testId="machine-types-search" />
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground uppercase tracking-wider font-bold">Show inactive</Label>
          <Switch checked={showInactive} onCheckedChange={setShowInactive} data-testid="machine-types-show-inactive" />
        </div>
      </div>

      <Card className="rounded-md overflow-hidden">
        <CardContent className="p-0">
          <table className="w-full text-sm" data-testid="machine-types-table">
            <thead className="bg-muted/50 border-b border-border">
              <tr className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                <th className="text-left py-2 px-3">Name</th>
                <th className="text-left py-2 px-3">Checklist items</th>
                <th className="text-left py-2 px-3">Parts</th>
                <th className="text-left py-2 px-3">Manuals</th>
                <th className="text-left py-2 px-3">Status</th>
                <th className="text-right py-2 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="py-8 text-center text-xs text-muted-foreground">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={6} className="py-8 text-center text-xs text-muted-foreground" data-testid="machine-types-empty">No machine types.</td></tr>
              ) : items.map((t) => (
                <tr key={t.id} data-testid={`machine-type-row-${t.id}`} className="border-b border-border last:border-0 hover:bg-muted/30">
                  <td className="py-2 px-3">
                    <Link to={withBackHref(`/machine-types/${t.id}`)} className="font-medium hover:text-primary flex items-center gap-2" data-testid={`machine-type-link-${t.id}`}>
                      <Boxes className="h-3.5 w-3.5 text-muted-foreground" /> {t.name}
                    </Link>
                  </td>
                  <td className="py-2 px-3 text-muted-foreground">{t.default_checklist?.length ?? 0}</td>
                  <td className="py-2 px-3 text-muted-foreground">{t.default_parts_picklist?.length ?? 0}</td>
                  <td className="py-2 px-3 text-muted-foreground">{t.manual_file_ids?.length ?? 0}</td>
                  <td className="py-2 px-3">
                    {t.active ? <StatusBadge tone="success">Active</StatusBadge> : <StatusBadge tone="muted">Inactive</StatusBadge>}
                  </td>
                  <td className="py-2 px-3 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`machine-type-actions-${t.id}`}><MoreHorizontal className="h-4 w-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/machine-types/${t.id}`)} data-testid={`machine-type-view-${t.id}`}><Eye className="h-4 w-4 mr-2" /> View</DropdownMenuItem>
                        {canWrite && <DropdownMenuItem onClick={() => navigate(`/machine-types/${t.id}/edit`)} data-testid={`machine-type-edit-${t.id}`}><Pencil className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>}
                        {canWrite && t.active && (<>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => handleDeactivate(t)} data-testid={`machine-type-deactivate-${t.id}`}><PowerOff className="h-4 w-4 mr-2" /> Deactivate</DropdownMenuItem>
                        </>)}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
